<template>
  <div class="document-uploader">
    <h3><i class="bi bi-cloud-arrow-up"></i> Upload Document</h3>

    <!-- Drag & Drop Zone -->
    <div
      class="drop-zone"
      :class="{
        'drop-zone--active': isDragging,
        'drop-zone--has-file': fileUploadData,
        'drop-zone--uploading': isUploading
      }"
      @dragenter.prevent="onDragEnter"
      @dragleave.prevent="onDragLeave"
      @dragover.prevent
      @drop.prevent="onDrop"
      @click="triggerFileInput"
    >
      <input
        type="file"
        ref="fileInput"
        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif"
        @change="onFileSelected"
        style="display: none;"
      />

      <!-- Upload Progress -->
      <div v-if="isUploading" class="upload-progress">
        <div class="progress-spinner"></div>
        <p>Uploading...</p>
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: uploadProgress + '%' }"></div>
        </div>
        <span class="progress-text">{{ uploadProgress }}%</span>
      </div>

      <!-- File Selected Preview -->
      <div v-else-if="fileUploadData" class="file-preview">
        <div class="file-icon">
          <i :class="getFileIcon(fileUploadData.name)"></i>
        </div>
        <div class="file-info">
          <p class="file-name">{{ fileUploadData.name }}</p>
          <p class="file-size">{{ formatFileSize(fileUploadData.size) }}</p>
        </div>
        <button class="remove-file-btn" @click.stop="removeFile" title="Remove file">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>

      <!-- Default Drop Zone Content -->
      <div v-else class="drop-zone-content">
        <div class="drop-icon">
          <i class="bi bi-cloud-arrow-up"></i>
        </div>
        <p class="drop-text">
          <span v-if="isDragging">Drop your file here</span>
          <span v-else>Drag & drop your file here or <strong>click to browse</strong></span>
        </p>
        <p class="file-types">Supported: PDF, DOC, DOCX, PNG, JPG, GIF</p>
      </div>
    </div>

    <!-- Physio Report Category Selection -->
    <div class="category-selection" v-if="documentCategory === 'physio-report'">
      <label class="form-label">Select document access</label>
      <select v-model="selectedDcoumetCategory" class="form-select">
        <option :value="null">Please select an option</option>
        <option value="physio">Physio / Admin</option>
        <option value="physio-player">Player</option>
      </select>
      <p class="upload-limit-warning" v-if="elementData && elementData.physio_document_id !== null && elementData.player_document_id !== null">
        <i class="bi bi-exclamation-triangle"></i>
        You can only upload up to two documents to a player's injury report. To add a new document, please delete an existing one first.
      </p>
    </div>

    <!-- Upload Button -->
    <div class="upload-actions">
      <button
        class="upload-btn"
        :class="{ 'upload-btn--disabled': !canUpload }"
        :disabled="!canUpload"
        @click="sendFile"
      >
        <i class="bi bi-cloud-arrow-up"></i>
        <span>{{ isUploading ? 'Uploading...' : 'Upload Document' }}</span>
      </button>
    </div>

    <!-- Upload Success Message -->
    <div v-if="uploadSuccess" class="upload-success">
      <i class="bi bi-check-circle"></i>
      <span>Document uploaded successfully!</span>
    </div>
  </div>
</template>

<script>
import Axios from "axios";

export default {
  name: "DocumentUploader",
  data() {
    return {
      fileUploadData: null,
      selectedDcoumetCategory: null,
      isDragging: false,
      isUploading: false,
      uploadProgress: 0,
      uploadSuccess: false,
      dragCounter: 0,
    };
  },
  props: {
    documentCategory: {
      type: String,
      default: null,
    },
    user_id: {
      type: Number,
      default: null,
    },
    club_id: {
      type: String,
      default: null,
    },
    elementData: {
      type: Object,
      default: null,
    },
    selectedDocumentData: {
      type: Object,
      default: null,
    },
  },
  computed: {
    canUpload() {
      if (!this.fileUploadData) return false;
      if (this.isUploading) return false;
      if (this.documentCategory === 'physio-report') {
        if (this.elementData &&
            this.elementData.physio_document_id !== null &&
            this.elementData.player_document_id !== null) {
          return false;
        }
      }
      return true;
    }
  },
  methods: {
    triggerFileInput() {
      if (!this.isUploading) {
        this.$refs.fileInput.click();
      }
    },

    onDragEnter(e) {
      e.preventDefault();
      this.dragCounter++;
      this.isDragging = true;
    },

    onDragLeave(e) {
      e.preventDefault();
      this.dragCounter--;
      if (this.dragCounter === 0) {
        this.isDragging = false;
      }
    },

    onDrop(e) {
      e.preventDefault();
      this.isDragging = false;
      this.dragCounter = 0;

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        this.handleFile(files[0]);
      }
    },

    onFileSelected(e) {
      const files = e.target.files;
      if (files.length > 0) {
        this.handleFile(files[0]);
      }
    },

    handleFile(file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/png', 'image/jpeg', 'image/gif'];
      const allowedExtensions = ['.pdf', '.doc', '.docx', '.png', '.jpg', '.jpeg', '.gif'];

      const extension = '.' + file.name.split('.').pop().toLowerCase();
      const isValidType = allowedTypes.includes(file.type) || allowedExtensions.includes(extension);

      if (!isValidType) {
        this.$toast.error("Please upload a PDF, DOC, DOCX, or image file.", {
          title: "Invalid File Type",
          solid: true,
        });
        return;
      }

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        this.$toast.error("File size must be less than 10MB.", {
          title: "File Too Large",
          solid: true,
        });
        return;
      }

      this.fileUploadData = file;
      this.uploadSuccess = false;
    },

    removeFile() {
      this.fileUploadData = null;
      this.$refs.fileInput.value = '';
      this.uploadSuccess = false;
    },

    getFileIcon(filename) {
      if (!filename) return 'bi bi-file-earmark';
      const ext = filename.split('.').pop().toLowerCase();
      const iconMap = {
        'pdf': 'bi bi-file-earmark-pdf-fill',
        'doc': 'bi bi-file-earmark-word-fill',
        'docx': 'bi bi-file-earmark-word-fill',
        'png': 'bi bi-file-earmark-image-fill',
        'jpg': 'bi bi-file-earmark-image-fill',
        'jpeg': 'bi bi-file-earmark-image-fill',
        'gif': 'bi bi-file-earmark-image-fill',
      };
      return iconMap[ext] || 'bi bi-file-earmark';
    },

    formatFileSize(bytes) {
      if (!bytes) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    async sendFile() {
      if (!this.canUpload) return;

      let userData;
      let API_SERVER = "";

      if (window.location.href.indexOf("8080") > -1) {
        API_SERVER = "http://www.localhost:8888/upload-document.php";
      } else {
        API_SERVER = "/personal-training-BE/upload-document.php";
      }

      if (this.selectedDocumentData !== null) {
        if (this.selectedDocumentData.category === "physio-report") {
          userData = {
            documentCategory: this.selectedDocumentData.category,
            user_id: this.user_id,
            club_id: this.club_id,
            element_id: this.elementData.id,
            physio_id: this.elementData.physio_id,
          };
        } else {
          userData = {
            documentCategory: this.selectedDocumentData.category,
            user_id: this.user_id,
            club_id: this.club_id,
            selectedDocumentData: JSON.stringify(this.selectedDocumentData),
          };
        }
      } else {
        userData = {
          documentCategory: this.selectedDcoumetCategory,
          user_id: this.user_id,
          club_id: this.club_id,
          element_id: this.elementData ? this.elementData.id : null,
        };
      }

      const formData = new FormData();
      formData.append("pdf", this.fileUploadData);

      Object.entries(userData).forEach(([k, v]) => {
        if (Array.isArray(v)) {
          v = JSON.stringify(v);
        }
        if (v !== null && v !== undefined) {
          formData.append(k, v);
        }
      });

      this.isUploading = true;
      this.uploadProgress = 0;

      try {
        const response = await Axios.post(API_SERVER, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            this.uploadProgress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
          },
        });

        // The endpoint answers with {success, error}. A 200 alone is not proof
        // the document was stored — rejected file types used to return 200
        // with no body, which showed a success toast for a document that was
        // never saved.
        if (response.data && response.data.success === false) {
          throw new Error(response.data.error || "Upload rejected.");
        }

        this.uploadSuccess = true;
        this.fileUploadData = null;
        this.$refs.fileInput.value = '';

        if (this.documentCategory !== "physio-report") {
          this.$emit("getDocumentUploadsCall");
        }

        this.$toast.success("Document uploaded successfully.", {
          title: "Uploaded",
          solid: true,
        });

        // Hide success message after 3 seconds
        setTimeout(() => {
          this.uploadSuccess = false;
        }, 3000);
      } catch (error) {
        // Prefer the server's explanation ("file too large", "unsupported
        // type") over a generic retry message.
        const serverError =
          error?.response?.data?.error ||
          (error?.response?.status === 413
            ? "That file is too large to upload."
            : null) ||
          error?.message;

        this.$toast.error(
          serverError || "Failed to upload document. Please try again.",
          {
            title: "Upload Failed",
            solid: true,
          }
        );
      } finally {
        this.isUploading = false;
        this.uploadProgress = 0;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.document-uploader {
  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    i {
      color: #28a745;
    }
  }
}

.drop-zone {
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: #f9fafb;
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: #28a745;
    background-color: #f0fdf4;
  }

  &--active {
    border-color: #28a745;
    background-color: #dcfce7;
    border-style: solid;

    .drop-icon i {
      transform: scale(1.2);
    }
  }

  &--has-file {
    border-color: #28a745;
    background-color: #f0fdf4;
    border-style: solid;
  }

  &--uploading {
    pointer-events: none;
    border-color: #3b82f6;
    background-color: #eff6ff;
  }
}

.drop-zone-content {
  .drop-icon {
    margin-bottom: 1rem;

    i {
      font-size: 3rem;
      color: #9ca3af;
      transition: all 0.3s ease;
    }
  }

  .drop-text {
    font-size: 1rem;
    color: #4b5563;
    margin-bottom: 0.5rem;

    strong {
      color: #28a745;
    }
  }

  .file-types {
    font-size: 0.8rem;
    color: #9ca3af;
    margin: 0;
  }
}

.drop-zone:hover .drop-icon i,
.drop-zone--active .drop-icon i {
  color: #28a745;
}

.file-preview {
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
  padding: 0.5rem;

  .file-icon {
    width: 50px;
    height: 50px;
    background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      font-size: 1.5rem;
      color: white;
    }
  }

  .file-info {
    flex: 1;
    text-align: left;
    min-width: 0;

    .file-name {
      font-weight: 600;
      color: #2c3e50;
      margin: 0 0 0.25rem 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .file-size {
      font-size: 0.85rem;
      color: #6b7280;
      margin: 0;
    }
  }

  .remove-file-btn {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: none;
    background-color: #fee2e2;
    color: #dc3545;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    flex-shrink: 0;

    &:hover {
      background-color: #dc3545;
      color: white;
    }
  }
}

.upload-progress {
  width: 100%;
  text-align: center;

  .progress-spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #e5e7eb;
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 1rem;
  }

  p {
    font-weight: 500;
    color: #3b82f6;
    margin-bottom: 0.75rem;
  }

  .progress-bar {
    width: 100%;
    height: 8px;
    background-color: #e5e7eb;
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 0.5rem;

    .progress-fill {
      height: 100%;
      background: linear-gradient(90deg, #3b82f6 0%, #2563eb 100%);
      border-radius: 4px;
      transition: width 0.3s ease;
    }
  }

  .progress-text {
    font-size: 0.9rem;
    color: #6b7280;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.category-selection {
  margin-top: 1.5rem;

  .form-label {
    display: block;
    font-weight: 500;
    color: #374151;
    margin-bottom: 0.5rem;
  }

  .form-select {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 1rem;
    background-color: white;
    cursor: pointer;
    transition: border-color 0.2s ease;

    &:focus {
      outline: none;
      border-color: #28a745;
      box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.1);
    }
  }

  .upload-limit-warning {
    margin-top: 0.75rem;
    padding: 0.75rem 1rem;
    background-color: #fef3cd;
    border: 1px solid #ffeeba;
    border-radius: 8px;
    color: #856404;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    i {
      color: #f0ad4e;
    }
  }
}

.upload-actions {
  margin-top: 1.5rem;

  .upload-btn {
    width: 100%;
    padding: 0.875rem 1.5rem;
    background: linear-gradient(135deg, #28a745 0%, #218838 100%);
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    transition: all 0.3s ease;
    box-shadow: 0 4px 14px rgba(40, 167, 69, 0.3);

    &:hover:not(.upload-btn--disabled) {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(40, 167, 69, 0.4);
    }

    &--disabled {
      background: #9ca3af;
      cursor: not-allowed;
      box-shadow: none;
    }

    i {
      font-size: 1.1rem;
    }
  }
}

.upload-success {
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: 8px;
  color: #155724;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  animation: fadeIn 0.3s ease;

  i {
    color: #28a745;
    font-size: 1.1rem;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// Bootstrap Icons font support
.bi, :deep(.bi) {
  font-family: "bootstrap-icons" !important;
  font-style: normal;
  font-weight: normal;
  display: inline-block;
  vertical-align: middle;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  speak: never;
}
</style>
